import React, { createContext, useContext, useState, useMemo } from 'react';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { 
  PhantomWalletAdapter, 
  SolflareWalletAdapter, 
  TorusWalletAdapter,
  Coin98WalletAdapter,
  CoinbaseWalletAdapter
} from '@solana/wallet-adapter-wallets';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { clusterApiUrl } from '@solana/web3.js';
import { ethers } from 'ethers';

// Import wallet adapter CSS
import '@solana/wallet-adapter-react-ui/styles.css';

const MultiChainContext = createContext();

// EVM Chains Configuration (for display purposes only)
const EVM_CHAINS = {
  1: { name: 'Ethereum', symbol: 'ETH' },
  56: { name: 'Binance Smart Chain', symbol: 'BNB' },
  137: { name: 'Polygon', symbol: 'MATIC' },
  42161: { name: 'Arbitrum', symbol: 'ETH' },
  10: { name: 'Optimism', symbol: 'ETH' },
  43114: { name: 'Avalanche', symbol: 'AVAX' },
  250: { name: 'Fantom', symbol: 'FTM' },
};

// Solana Networks
const SOLANA_NETWORKS = {
  mainnet: { name: 'Solana Mainnet', endpoint: clusterApiUrl('mainnet-beta') },
  devnet: { name: 'Solana Devnet', endpoint: clusterApiUrl('devnet') },
  testnet: { name: 'Solana Testnet', endpoint: clusterApiUrl('testnet') },
};

const MultiChainProviderInner = ({ children }) => {
  const [selectedChain, setSelectedChain] = useState('solana'); // 'solana' or 'evm'
  const [selectedEVMChain, setSelectedEVMChain] = useState(1); // Ethereum mainnet
  const [selectedSolanaNetwork, setSelectedSolanaNetwork] = useState('mainnet');
  
  // EVM Wallet State
  const [evmProvider, setEvmProvider] = useState(null);
  const [evmSigner, setEvmSigner] = useState(null);
  const [evmAccount, setEvmAccount] = useState(null);
  const [evmBalance, setEvmBalance] = useState(null);

  // Solana Configuration (same for both chains)
  const solanaNetwork = WalletAdapterNetwork.Mainnet;
  const solanaEndpoint = useMemo(() => clusterApiUrl(solanaNetwork), [solanaNetwork]);
  const solanaWallets = useMemo(() => [
    new PhantomWalletAdapter(),
    new SolflareWalletAdapter(),
    new TorusWalletAdapter(),
    new Coin98WalletAdapter(),
    new CoinbaseWalletAdapter()
  ], []);

  // EVM Chain RPC URLs
  const EVM_CHAIN_RPCS = {
    1: 'https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
    56: 'https://bsc-dataseed.binance.org/',
    137: 'https://polygon-rpc.com/',
    42161: 'https://arb1.arbitrum.io/rpc',
    10: 'https://mainnet.optimism.io',
    43114: 'https://api.avax.network/ext/bc/C/rpc',
    250: 'https://rpc.ftm.tools/',
  };

  // Get current wallet info
  const getWalletInfo = () => {
    if (selectedChain === 'evm') {
      return {
        type: 'evm',
        isConnected: !!evmAccount,
        account: evmAccount,
        balance: evmBalance,
        provider: evmProvider,
        signer: evmSigner,
      };
    }
    return {
      type: 'solana',
      isConnected: false, // Will be handled by Solana wallet adapter
    };
  };

  // EVM Wallet Connection Functions
  const connectEVMWallet = async (walletId) => {
    try {
      console.log('🔌 connectEVMWallet called with walletId:', walletId);
      
      let walletProvider = null;
      let walletDetected = false;

      // First, try to detect any available Ethereum wallet
      if (typeof window.ethereum !== 'undefined') {
        console.log('✅ Ethereum wallet detected');
        walletProvider = new ethers.providers.Web3Provider(window.ethereum);
        walletDetected = true;
      } else if (typeof window.BinanceChain !== 'undefined') {
        console.log('✅ Binance Chain detected');
        walletProvider = new ethers.providers.Web3Provider(window.BinanceChain);
        walletDetected = true;
      } else {
        console.log('❌ No Ethereum wallet detected');
        // For mobile, try to open the specific wallet app
        const wallet = getWalletConfig(walletId);
        if (wallet) {
          const appStoreUrl = getAppStoreUrl(wallet);
          window.open(appStoreUrl, '_blank');
          throw new Error(`${wallet.name} not installed. Redirecting to download page...`);
        } else {
          throw new Error('No compatible wallet found. Please install MetaMask or another Ethereum wallet.');
        }
      }

      if (!walletDetected || !walletProvider) {
        throw new Error('Failed to initialize wallet provider');
      }

      console.log('🔐 Requesting account access...');
      // Request account access - this should trigger the wallet popup
      const accounts = await walletProvider.send('eth_requestAccounts', []);
      const account = accounts[0];
      const signer = walletProvider.getSigner();
      
      console.log('✅ Account access granted:', account);
      console.log('✅ Signer created:', !!signer);

      // Switch to selected chain if needed
      const currentChainId = await walletProvider.send('eth_chainId', []);
      const targetChainId = `0x${selectedEVMChain.toString(16)}`;
      
      console.log('🔗 Chain switching:', { currentChainId, targetChainId, selectedEVMChain });

      if (currentChainId !== targetChainId) {
        try {
          console.log('🔄 Switching to target chain...');
          await walletProvider.send('wallet_switchEthereumChain', [
            { chainId: targetChainId }
          ]);
          console.log('✅ Chain switched successfully');
        } catch (switchError) {
          console.log('⚠️ Chain switch failed, attempting to add chain...', switchError);
          // If chain doesn't exist, add it
          if (switchError.code === 4902) {
            const chainConfig = getChainConfig(selectedEVMChain);
            await walletProvider.send('wallet_addEthereumChain', [chainConfig]);
            console.log('✅ Chain added successfully');
          } else {
            throw switchError;
          }
        }
      }

      console.log('💾 Setting EVM state...');
      setEvmProvider(walletProvider);
      setEvmSigner(signer);
      setEvmAccount(account);
      
      // Fetch balance
      console.log('💰 Fetching balance...');
      const balance = await walletProvider.getBalance(account);
      const formattedBalance = ethers.utils.formatEther(balance);
      setEvmBalance(formattedBalance);
      console.log('💰 Balance set:', formattedBalance, 'ETH');

      console.log('✅ EVM wallet connection completed successfully');
      return { success: true };
    } catch (err) {
      console.error('❌ EVM wallet connection failed:', err);
      return { success: false, error: err.message };
    }
  };

  // Helper function to get wallet configuration
  const getWalletConfig = (walletId) => {
    const walletConfigs = {
      metamask: {
        name: 'MetaMask',
        downloadUrl: 'https://metamask.io/download/',
        appStoreUrl: 'https://apps.apple.com/app/metamask-blockchain-wallet/id1438144202',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=io.metamask'
      },
      trust: {
        name: 'Trust Wallet',
        downloadUrl: 'https://trustwallet.com/',
        appStoreUrl: 'https://apps.apple.com/app/trust-crypto-bitcoin-wallet/id1288339409',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.wallet.crypto.trust'
      },
      coinbase: {
        name: 'Coinbase Wallet',
        downloadUrl: 'https://wallet.coinbase.com/',
        appStoreUrl: 'https://apps.apple.com/app/coinbase-wallet/id1278383455',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=org.toshi'
      },
      binance: {
        name: 'Binance Wallet',
        downloadUrl: 'https://www.bnbchain.org/en/binance-wallet',
        appStoreUrl: 'https://apps.apple.com/app/binance-buy-bitcoin-crypto/id1436799971',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.binance.dev'
      },
      okx: {
        name: 'OKX Wallet',
        downloadUrl: 'https://www.okx.com/web3',
        appStoreUrl: 'https://apps.apple.com/app/okx-buy-bitcoin-crypto/id1327268470',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.okinc.okex.gp'
      },
      bitget: {
        name: 'Bitget Wallet',
        downloadUrl: 'https://web3.bitget.com/',
        appStoreUrl: 'https://apps.apple.com/app/bitget-buy-crypto-bitcoin/id1487644418',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.bitget.wallet'
      },
      brave: {
        name: 'Brave Wallet',
        downloadUrl: 'https://brave.com/wallet/',
        appStoreUrl: 'https://apps.apple.com/app/brave-browser/id1052879175',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.brave.browser'
      },
      bybit: {
        name: 'Bybit Wallet',
        downloadUrl: 'https://www.bybit.com/en/web3',
        appStoreUrl: 'https://apps.apple.com/app/bybit-buy-sell-crypto/id1488296982',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.bybit.app'
      },
      cryptodotcom: {
        name: 'Crypto.com Wallet',
        downloadUrl: 'https://crypto.com/defi-wallet',
        appStoreUrl: 'https://apps.apple.com/app/crypto-com-buy-bitcoin/id1262148500',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.crypto.exchange'
      },
      blockchain: {
        name: 'Blockchain.com',
        downloadUrl: 'https://www.blockchain.com/wallet',
        appStoreUrl: 'https://apps.apple.com/app/blockchain-bitcoin-wallet/id493253309',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=piuk.blockchain.android'
      },
      safepal: {
        name: 'SafePal Wallet',
        downloadUrl: 'https://www.safepal.com/download',
        appStoreUrl: 'https://apps.apple.com/app/safepal-wallet/id1548297139',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=io.safepal.wallet'
      },
      uniswap: {
        name: 'Uniswap Wallet',
        downloadUrl: 'https://wallet.uniswap.org/',
        appStoreUrl: 'https://apps.apple.com/app/uniswap-wallet/id6443944476',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=org.uniswap.mobile'
      },
      zeal: {
        name: 'Zeal Wallet',
        downloadUrl: 'https://zeal.app/',
        appStoreUrl: 'https://apps.apple.com/app/zeal-wallet/id6443944476',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.zeal.wallet'
      },
      zerion: {
        name: 'Zerion Wallet',
        downloadUrl: 'https://zerion.io/',
        appStoreUrl: 'https://apps.apple.com/app/zerion-defi-wallet/id1456732565',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=io.zerion.android'
      }
    };
    return walletConfigs[walletId];
  };

  // Helper function to get app store URL based on platform
  const getAppStoreUrl = (wallet) => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      return wallet.appStoreUrl;
    } else if (userAgent.includes('android')) {
      return wallet.playStoreUrl;
    } else {
      return wallet.downloadUrl;
    }
  };

  const getChainConfig = (chainId) => {
    const chain = EVM_CHAINS[chainId];
    return {
      chainId: `0x${chainId.toString(16)}`,
      chainName: chain.name,
      nativeCurrency: {
        name: chain.symbol,
        symbol: chain.symbol,
        decimals: 18,
      },
      rpcUrls: [EVM_CHAIN_RPCS[chainId]],
      blockExplorerUrls: [getExplorerUrl(chainId)],
    };
  };

  const getExplorerUrl = (chainId) => {
    const explorers = {
      1: 'https://etherscan.io',
      56: 'https://bscscan.com',
      137: 'https://polygonscan.com',
      42161: 'https://arbiscan.io',
      10: 'https://optimistic.etherscan.io',
      43114: 'https://snowtrace.io',
      250: 'https://ftmscan.com',
    };
    return explorers[chainId] || 'https://etherscan.io';
  };

  const disconnectEVMWallet = () => {
    setEvmProvider(null);
    setEvmSigner(null);
    setEvmAccount(null);
    setEvmBalance(null);
  };

  const switchEVMChain = async (chainId) => {
    try {
      if (!evmProvider) {
        setSelectedEVMChain(chainId);
        return;
      }

      const targetChainId = `0x${chainId.toString(16)}`;
      
      try {
        await evmProvider.send('wallet_switchEthereumChain', [
          { chainId: targetChainId }
        ]);
        setSelectedEVMChain(chainId);
      } catch (switchError) {
        // If chain doesn't exist, add it
        if (switchError.code === 4902) {
          const chainConfig = getChainConfig(chainId);
          await evmProvider.send('wallet_addEthereumChain', [chainConfig]);
          setSelectedEVMChain(chainId);
        } else {
          throw switchError;
        }
      }
    } catch (err) {
      console.error('Failed to switch EVM chain:', err);
      throw err;
    }
  };

  const value = {
    // Chain selection
    selectedChain,
    setSelectedChain,
    selectedEVMChain,
    setSelectedEVMChain,
    selectedSolanaNetwork,
    setSelectedSolanaNetwork,
    
    // EVM specific
    EVM_CHAINS,
    EVM_CHAIN_RPCS,
    evmProvider,
    evmSigner,
    evmAccount,
    evmBalance,
    connectEVMWallet,
    disconnectEVMWallet,
    switchEVMChain,
    
    // Solana specific (used for both)
    SOLANA_NETWORKS,
    solanaEndpoint,
    solanaWallets,
    
    // Unified interface
    getWalletInfo,
  };

  return (
    <MultiChainContext.Provider value={value}>
      {children}
    </MultiChainContext.Provider>
  );
};

export const MultiChainProvider = ({ children }) => {
  return (
    <ConnectionProvider endpoint={clusterApiUrl('mainnet-beta')}>
      <WalletProvider 
        wallets={[
          new PhantomWalletAdapter(),
          new SolflareWalletAdapter(),
          new TorusWalletAdapter(),
          new Coin98WalletAdapter(),
          new CoinbaseWalletAdapter()
        ]} 
        autoConnect={true}
      >
        <WalletModalProvider featuredWallets={99}>
          <MultiChainProviderInner>
            {children}
          </MultiChainProviderInner>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

export const useMultiChain = () => {
  const context = useContext(MultiChainContext);
  if (!context) {
    throw new Error('useMultiChain must be used within a MultiChainProvider');
  }
  return context;
};